import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LlmConfigList } from '@/components/llm/LlmConfigList';
import { LlmConfigForm } from '@/components/llm/LlmConfigForm';
import {
  useLlmConfigs,
  useCreateLlmConfig,
  useUpdateLlmConfig,
  useDeleteLlmConfig,
} from '@/hooks/use-llm-config';
import { useAuth } from '@/hooks/use-auth';
import { filterByTenant } from '@/context/auth-context';
import type { LlmConfig } from '@/types';
import type { LlmConfigFormData } from '@/schemas/llm-config.schema';

export default function LlmConfigPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingConfig, setEditingConfig] = useState<LlmConfig | undefined>();

  const { isTenant, serviceId } = useAuth();
  const { data: configs, isLoading, isError, error } = useLlmConfigs();
  const createMutation = useCreateLlmConfig();
  const updateMutation = useUpdateLlmConfig();
  const deleteMutation = useDeleteLlmConfig();

  const handleCreate = () => {
    setFormMode('create');
    setEditingConfig(undefined);
    setFormOpen(true);
  };

  const handleEdit = (config: LlmConfig) => {
    setFormMode('edit');
    setEditingConfig(config);
    setFormOpen(true);
  };

  const handleDelete = (configServiceId: string) => {
    deleteMutation.mutate(configServiceId);
  };

  const handleSubmit = (data: LlmConfigFormData) => {
    if (formMode === 'create') {
      createMutation.mutate(data, {
        onSuccess: () => setFormOpen(false),
      });
    } else if (editingConfig) {
      // If apiKey is empty on edit, send null so the server keeps the existing key
      const submitData = {
        ...data,
        apiKey: data.apiKey && data.apiKey.length > 0 ? data.apiKey : undefined,
      };
      updateMutation.mutate(
        { serviceId: editingConfig.serviceId, data: submitData },
        { onSuccess: () => setFormOpen(false) },
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12 text-destructive">
        Failed to load configurations: {error?.message ?? 'Unknown error'}
      </div>
    );
  }

  // Tenant users only see their own configuration
  const displayedConfigs = isTenant
    ? filterByTenant(Array.isArray(configs) ? configs : [], serviceId, 'serviceId')
    : Array.isArray(configs) ? configs : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">LLM Configuration</h1>
        {!isTenant && (
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Create Configuration
          </Button>
        )}
      </div>

      <LlmConfigList
        configs={displayedConfigs}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isDeleting={deleteMutation.isPending}
      />

      <LlmConfigForm
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        initialData={editingConfig}
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}
